import { useDispatch, useSelector } from "react-redux";
import { loginItemsSelector, setPublishFlow } from "./redux/login";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import Loader from "./components/loader/Loader";
import { Slide, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./css/toast.css";
import ToastIcon from "./components/Common/ToastIcon";
import PrivateRoute from "./components/Common/PrivateRoute";
import UserDashboardLayout from "./components/layout/UserDashboardLayout";
const HomePage = lazy(() => import("./pages/HomePage"));
const HomeProperties = lazy(() => import("./pages/HomeProperties"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const About = lazy(() => import("./pages/About"));
const Featured = lazy(() => import("./pages/Featured"));
const Services = lazy(() => import("./pages/Services"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Faq = lazy(() => import("./pages/Faq"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MyCommissions = lazy(() =>
  import("./dashboard/agent-dashboard/commissions/MyCommissions")
);
const AgentSchedule = lazy(() =>
  import("./dashboard/agent-dashboard/schedule/AgentSchedule")
);
const AgentDashboard = lazy(() =>
  import("./dashboard/agent-dashboard/AgentDashboard")
);
const AgentInvite = lazy(() => import("./pages/AgentInvite"));
// const AddAgent = lazy(() => import("./dashboard/agent-dashboard/AddAgent"));
const UploadDocuments = lazy(() =>
  import("./dashboard/agent-dashboard/UploadDocuments")
);

const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const UploadPropertyImagesAndVideos = lazy(() =>
  import("./dashboard/agent-dashboard/post-property/UploadPropertyImages")
);
const PropertyView = lazy(() =>
  import("./dashboard/Buy-property/PropertyView")
);
const PostProperty = lazy(() => import("./pages/UserDashboard/PostProperty"));
const Wishlists = lazy(() => import("./pages/UserDashboard/Wishlists"));
const MyDeals = lazy(() => import("./pages/UserDashboard/MyDeals"));
const MyScheduleVisits = lazy(() =>
  import("./pages/UserDashboard/MyScheduleVisits")
);
const MyProperty = lazy(() => import("./pages/UserDashboard/MyProperty"));
const UserDashboard = lazy(() => import("./pages/UserDashboard/Dashboard"));
const Settings = lazy(() =>
  import("./dashboard/agent-dashboard/settings/Settings")
);
const UserSettings = lazy(() =>
  import("./pages/UserDashboard/user-settings/UserSettings")
);
const PhoneNudge = lazy(() => import("./components/Common/PhoneNudge"));
const LookingForDialog = lazy(() =>
  import("./components/Dialog/LookingForDialog")
);
const AgentList = lazy(() => import("./dashboard/agent-dashboard/MyAgentList"));
const UpdateNewPassword = lazy(() => import("./pages/UpdateNewPassword"));
const PublishFlowDialog = () => {
  const dispatch = useDispatch();
  const { publishFlowOpen } = useSelector(loginItemsSelector);

  if (!publishFlowOpen) return null;

  // Its own Suspense with no fallback. Inside the page's Suspense, loading
  // this chunk replaced the whole route tree with the full-screen loader for
  // an instant — which is the flash you get when the dialog opens.
  return (
    <Suspense fallback={null}>
      <LookingForDialog
        open
        onClose={() => dispatch(setPublishFlow(false))}
      />
    </Suspense>
  );
};

const AppRoutes = () => {
  return (
    <Suspense fallback={<Loader />}>
      {/* No progress bar and no drag: a sentence that stays a couple of
          seconds and can be dismissed with a click. Three at most, newest
          on top, so a burst of answers does not stack down the screen. */}
      <ToastContainer
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable={false}
        limit={3}
        transition={Slide}
        icon={ToastIcon}
      />
      {/* The picker is a dialog, so it opens where the visitor already is.
          It used to live inside the /user-dashboard page, which is why
          clicking "list a property" changed route before asking anything. */}
      <PublishFlowDialog />
      {/* Asked once of a customer whose account has no phone number — the
          ones that came in through Google. Global for the same reason the
          picker is: it has to appear wherever the sign-in landed them. */}
      <Suspense fallback={null}>
        <PhoneNudge />
      </Suspense>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/about" element={<About />} />
        <Route path="/about-us" element={<Navigate to="/about" replace />} />
        <Route path="/home-properties" element={<HomeProperties />} />
        <Route path="/featured" element={<Featured />} />
        <Route path="/services" element={<Services />} />
        <Route path="/terms-conditions" element={<TermsConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/faq" element={<Faq />} />
        <Route
          path="/user-dashboard"
          element={<PrivateRoute Children={UserDashboard} />}
        />
        <Route
          path="/agent-dashboard"
          element={
            <PrivateRoute isAdminRoute={true} Children={AgentDashboard} />
          }
        />
        <Route
          path="/agent-settings"
          element={<PrivateRoute isAdminRoute={true} Children={Settings} />}
        />
        
        <Route
          path="/agent-signup"
          element={<AgentInvite />}
        />
        <Route
          path="/post-property"
          element={<PrivateRoute Children={PostProperty} />}
        />
         <Route
          path="/user-settings"
          element={<PrivateRoute  Children={UserSettings} />}
        />
        <Route
          path="/user-schedule-visit"
          element={<PrivateRoute Children={MyScheduleVisits} />}
        />
        <Route path="/my-deals" element={<PrivateRoute Children={MyDeals} />} />
        <Route
          path="/wishlists"
          element={<PrivateRoute Children={Wishlists} />}
        />
        <Route
          path="/my-property"
          element={<PrivateRoute Children={MyProperty} />}
        />
        <Route
          path="/view-property/:propertyId"
          element={<PropertyView />}
        />
        {/* The agent's view of a property is the same page; what differs
            is gathered behind isAgent inside it. */}
        <Route
          path="/view-property-agent/:propertyId"
          element={
            <PrivateRoute
              isAdminRoute={true}
              Children={() => <PropertyView isAgent />}
            />
          }
        />
        <Route
          path="/property-view/:propertyId"
          element={<PropertyView />}
        />
        <Route
          path="/upload-image"
          element={<UploadPropertyImagesAndVideos />}
        />
        <Route
          path="/upload-image"
          element={<UploadPropertyImagesAndVideos />}
        />
        <Route path="/property-view" element={<PropertyView />} />
        <Route path="/user" element={<PostProperty />} />
        <Route path="/agent" element={<AgentDashboard />} />
        <Route path="/upload-documents" element={<UploadDocuments />} />
        <Route
          path="/agent-schedule"
          element={<PrivateRoute isAdminRoute={true} Children={AgentSchedule} />}
        />
        {/* Visits and audits were the same work on five screens, two of
            them running the same query. They are one page now; the old
            paths keep working. */}
        <Route path="/manage-property-visit" element={<Navigate to="/agent-schedule" replace />} />
        <Route path="/manage-property-audit" element={<Navigate to="/agent-schedule" replace />} />
        <Route path="/audit-property" element={<Navigate to="/agent-schedule" replace />} />
        <Route path="/property-to-be-audited" element={<Navigate to="/agent-schedule" replace />} />
        <Route path="/schedule-property-audited" element={<Navigate to="/agent-schedule" replace />} />
        <Route path="/book-visit" element={<Navigate to="/agent-schedule" replace />} />
        <Route path="/scheduled-visit" element={<Navigate to="/agent-schedule" replace />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/my-commissions" element={<PrivateRoute isAdminRoute={true} Children={MyCommissions} />} />
        {/* Sales and rentals were two pages of the same table. Old links
            and bookmarks land on the one that replaced them. */}
        <Route path="/my-commission-sale" element={<Navigate to="/my-commissions" replace />} />
        <Route path="/my-commission-rent" element={<Navigate to="/my-commissions" replace />} />
        <Route path="/my-agents-list" element={<PrivateRoute isAdminRoute={true} Children={AgentList} />} />
        <Route path="/update-new-password" element={<UpdateNewPassword />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
